import { MUTATIONS, useMutation } from "../../../api";
import type { WorkshopId, WorkshopParticipant } from "../../../api/workshop";
import UserDisplay from "../../../design-system/primitives/components/UserDisplay/UserDisplay";
import IconButton from "../../../design-system/primitives/components/IconButton/IconButton";
import Tooltip from "../../../design-system/primitives/components/Tooltip/Tooltip";
import { useConfirmAction } from "../../../design-system/primitives/components/AlertDialog/useConfirmAction";
import { useRemoveFromWorkshop } from "./useRemoveFromWorkshop";

type TeacherRowProps = {
  teacher: WorkshopParticipant;
  workshop: { id: WorkshopId };
  isOwner: boolean;
  /** Whether the "Verwaltung übertragen" action is shown — the caller decides who may transfer. */
  canTransferOwnership: boolean;
  /** Whether the "Entfernen" action is shown — the caller decides who may remove. */
  canRemove: boolean;
};

/**
 * One colleague's row, wired to the real workshop mutations: transfer
 * ownership and remove from workshop, both AlertDialogs (Figma's own
 * component doc names "Radix AlertDialog-Pattern" for both — a reversible
 * administrative action for transfer, a destructive one for remove).
 */
const TeacherRow = ({
  teacher,
  workshop,
  isOwner,
  canTransferOwnership,
  canRemove,
}: TeacherRowProps) => {
  const [doTransferOwnership] = useMutation(
    MUTATIONS.transferWorkshopOwnership,
  );
  const transferOwnership = useConfirmAction({
    onConfirm: () =>
      doTransferOwnership({ id: workshop.id, userId: teacher.id }),
    variant: "default",
    title: `Verwaltung an ${teacher.name} übertragen?`,
    description:
      "Du kannst danach die Lernreise nicht mehr löschen, bist aber weiterhin Teil der Lernreise.",
    errorDescription:
      "Die Verwaltung konnte nicht übertragen werden. Bitte versuche es erneut.",
    actionLabel: "Übertragen",
  });
  const removeFromWorkshop = useRemoveFromWorkshop(workshop.id, teacher);

  return (
    <>
      <UserDisplay
        user={teacher}
        surface="base"
        highlight={isOwner}
        actionsSlot={
          canTransferOwnership || canRemove ? (
            <>
              {canTransferOwnership && (
                <Tooltip content="Verwaltung übertragen">
                  <IconButton
                    icon="users"
                    ariaLabel={`Verwaltung an ${teacher.name} übertragen`}
                    variant="secondary"
                    onClick={transferOwnership.open}
                  />
                </Tooltip>
              )}

              {canRemove && (
                <Tooltip content="Entfernen">
                  <IconButton
                    icon="x"
                    ariaLabel={`${teacher.name} entfernen`}
                    variant="secondary"
                    onClick={removeFromWorkshop.open}
                  />
                </Tooltip>
              )}
            </>
          ) : undefined
        }
      />

      {transferOwnership.dialogElement}
      {removeFromWorkshop.dialogElement}
    </>
  );
};

export default TeacherRow;
